import { useTranslation } from "react-i18next";
import PageWrapper from "../../components/PageWrapper";
import CollapsibleCard from "../attendance-system/CollapsibleCard";
import SectionDivider from "../../components/SectionDivider";
import TextDiv from "../../components/TextDiv";
import RenderMatchStatus from "../../components/RenderMatchStatus";
import { DoneIcon, FailedIcon, WarningIcon } from "../../assets/registtyIcons/icons";
import { formatDate } from "../../utils/toDate";
import { useLocation } from "react-router-dom";
import { formatString } from "../../utils/formatString";
import SectionDividerWithNoLine from "../../components/SectionDividerWithNoLine";
import { extractNumericValue } from "../../utils/extractNumericValues";
import { useState } from "react";
import Loader, { LOADER_SIZES } from "../../components/Loader";
import { hasCurrencySymbol } from "../../utils/currencySymbolChecker";
import { useContext } from "react";
import { UserAuth } from "../../utils/AuthContext";
import axiosClient from "../../services/axiosClient";
import Tooltip from "../../components/Tooltip";
import HsCodeViewer from "../../components/hsCodeViewer";
import infoBlue from "../../assets/info-blue.svg";

const CompanyAnalyzeApplicationPage = () => {
  const { t } = useTranslation("general");
  const location = useLocation();
  const { userData } = useContext(UserAuth);

  const [analyzedData, setAnalyzedData] = useState(
    location.state?.data?.analyzedData || null
  );
  const [loadingHsCode, setLoadingHsCode] = useState(false);
  const [invoiceHsCodes, setInvoiceHsCodes] = useState([]);
  const [itemHsCodes, setItemHsCodes] = useState({});

  const getHsCodes = async (hscode, setCodesCallback) => {
    try {
      setLoadingHsCode(true);
      const response = await axiosClient.get(
        `/hsCodes/getSpecificCode?code=${hscode}`
      );

      const result = [
        {
          code: response?.data?.parent?.code,
          description: response?.data?.parent?.description,
        },
        {
          code: response?.data?.child1?.code,
          description: response?.data?.child1?.description,
        },
        {
          code: response?.data?.child2?.code,
          description: response?.data?.child2?.description,
        },
        {
          code: response?.data?.child3?.code,
          description: response?.data?.child3?.description,
        },
      ];

      setCodesCallback(result);
    } catch (error) {
      console.log("error getting hs codes", error);
    } finally {
      setLoadingHsCode(false);
    }
  };

  const supplierInvoiceDetails = analyzedData?.invoice?.supplier || {};
  const supplierWebsiteDetails = analyzedData?.supplierWebsiteScraper || {};
  const items = analyzedData?.invoice?.items;

  if (!analyzedData) {
    return (
      <PageWrapper withBackButton title={t("analyzeInvoices")}>
        <Loader />
      </PageWrapper>
    );
  }

  return (
    <PageWrapper withBackButton title={t("analyzeInvoices")}>
      <div className="o-row o-row--gap-size_100 t-bold_bold u-margin--top_400">
        <span>
          {userData?.userType === "organization"
            ? `${t("organization")}:`
            : `${t("user")}:`}
        </span>
        <span>{`${localStorage.getItem("username")}`}</span>
      </div>

      <div className="o-column o-column--gap-size_400">
        {/* Supplier Details */}
        <div>
          <SectionDivider title={"supplier"} />
          <CollapsibleCard
            title={`${t("supplierDetails")} (${t("receiver")})`}
            collapsed={true}
            body={
              <>
                {/* Supplier name section */}
                <section>
                  <SectionDivider title={t("supplierName")} noMargin />
                  <div className="o-column o-column--gap-size_500 u-margin--top_400">
                    <TextDiv
                      title="supplierNameInInvoice"
                      value={supplierInvoiceDetails.name}
                      divClasses="o-column-row-switcher"
                      pClasses="t-bold_semi-bold u-width--min-width_300"
                    />
                  </div>
                </section>

                {/* Address section */}
                <section className="u-margin--top_500">
                  <SectionDivider title={t("address")} />
                  <div className="o-column o-column--gap-size_500">
                    <TextDiv
                      title="addressInInvoice"
                      value={supplierInvoiceDetails.address}
                      divClasses="o-column-row-switcher"
                      pClasses="t-bold_semi-bold u-width--min-width_300"
                      spanClasses="dir-ltr t-text-align_center"
                    />
                  </div>
                </section>

                {/* Phone number section */}
                <section className="u-margin--top_500">
                  <SectionDivider title={t("phoneNumber")} />
                  <div className="o-column o-column--gap-size_500">
                    <TextDiv
                      title="phoneNumberInInvoice"
                      value={supplierInvoiceDetails.phoneNumber}
                      divClasses="o-column-row-switcher"
                      pClasses="t-bold_semi-bold u-width--min-width_300"
                      spanClasses="dir-ltr"
                    />
                  </div>
                </section>

                {/* Email section */}
                <section className="u-margin--top_500">
                  <SectionDivider title={t("email")} />
                  <div className="o-column o-column--gap-size_500">
                    <TextDiv
                      title="emailInInvoice"
                      value={supplierInvoiceDetails.email}
                      divClasses="o-column-row-switcher"
                      pClasses="t-bold_semi-bold u-width--min-width_300"
                      spanClasses="dir-ltr"
                    />
                  </div>
                </section>

                {/* Website section */}
                <section className="u-margin--top_500">
                  <SectionDivider title={t("website")} />
                  <div className="o-column o-column--gap-size_500">
                    <div className="o-column o-column--gap-size_200">
                      <section className="o-column-row-switcher ">
                        <span className="u-width--min-width_300 t-bold_semi-bold">
                          {t("websiteInInvoice")}:{" "}
                        </span>
                        {supplierInvoiceDetails.website ? (
                          <a
                            href={
                              supplierInvoiceDetails.website &&
                              supplierInvoiceDetails.website.startsWith("http")
                                ? supplierInvoiceDetails.website
                                : `http://${supplierInvoiceDetails.website}`
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                            className="link-color t-bold_bold"
                          >
                            {supplierInvoiceDetails.website}
                          </a>
                        ) : (
                          <span className="not-working-website">
                            {t(supplierInvoiceDetails.website?.urlStatus)}
                          </span>
                        )}
                      </section>

                      {supplierWebsiteDetails.urlStatus === "working" && (
                        <TextDiv
                          title="websiteCreationDate"
                          value={formatDate(
                            supplierWebsiteDetails.foundationDate
                          )}
                          divClasses="o-column-row-switcher"
                          pClasses="u-width--min-width_300"
                        />
                      )}

                      <section className="o-column-row-switcher">
                        <span className="u-width--min-width_300">
                          {t("websiteStatus")}:{" "}
                        </span>

                        {supplierWebsiteDetails.urlStatus !==
                        "noURLProvided" ? (
                          <div className="o-row o-row--vertical-align_center o-row--gap-size_100">
                            {supplierWebsiteDetails.urlStatus === "working" ? (
                              <DoneIcon width={20} height={20} />
                            ) : supplierWebsiteDetails.urlStatus ===
                              "notWorking" ? (
                              <FailedIcon width={20} height={20} />
                            ) : supplierWebsiteDetails.urlStatus !==
                              "notExist" ? (
                              <WarningIcon width={20} height={20} />
                            ) : (
                              <FailedIcon width={20} height={20} />
                            )}

                            <span
                              className={
                                supplierWebsiteDetails.urlStatus === "working"
                                  ? "working-website"
                                  : supplierWebsiteDetails.urlStatus ===
                                    "notExist"
                                  ? "cannot-reach-website"
                                  : "cannot-reach-website"
                              }
                            >
                              {t(
                                supplierWebsiteDetails?.urlStatus?.toLowerCase() ||
                                  "notExist"
                              )}
                            </span>
                          </div>
                        ) : null}
                      </section>
                    </div>
                  </div>
                </section>
              </>
            }
          />
        </div>

        {/* Invoice Details */}
        <div>
          <SectionDivider title={"invoice"} />
          <CollapsibleCard
            title={t("invoiceDetails")}
            collapsed={true}
            body={
              <>
                <section>
                  <SectionDivider title={t("invoice")} noMargin />
                  <div className="o-column o-column--gap-size_400 u-margin--top_400">
                    <TextDiv
                      title="invoiceNumber"
                      value={analyzedData?.invoice?.invoiceNumber || ""}
                      divClasses="o-column-row-switcher"
                      pClasses="t-bold_semi-bold u-width--min-width_300"
                    />

                    <TextDiv
                      title="invoiceDate"
                      value={analyzedData?.invoice?.invoiceDate || ""}
                      divClasses="o-column-row-switcher"
                      pClasses="t-bold_semi-bold u-width--min-width_300"
                    />
                    <TextDiv
                      title="countryOfOrigin"
                      value={
                        analyzedData?.invoice?.originOfProduct || (
                          <div className="o-row o-row--vertical-align_end o-row--gap-size_100">
                            <FailedIcon width={20} height={20} />
                            <span className={"not-working-website"}>
                              {t("notFound")}
                            </span>
                          </div>
                        )
                      }
                      divClasses="o-column-row-switcher"
                      pClasses="t-bold_semi-bold u-width--min-width_300"
                    />

                    <TextDiv
                      title="shippingTerms"
                      value={
                        analyzedData?.invoice?.terms?.[0]?.shippingTerms || (
                          <div className="o-row o-row--vertical-align_end o-row--gap-size_100">
                            <FailedIcon width={20} height={20} />
                            <span className={"not-working-website"}>
                              {t("notFound")}
                            </span>
                          </div>
                        )
                      }
                      divClasses="o-column-row-switcher"
                      pClasses="t-bold_semi-bold u-width--min-width_300"
                    />

                    <TextDiv
                      title="paymentTerms"
                      value={
                        analyzedData?.invoice?.terms?.[0]?.paymentTerms || (
                          <div className="o-row o-row--vertical-align_end o-row--gap-size_100">
                            <FailedIcon width={20} height={20} />
                            <span className={"not-working-website"}>
                              {t("notFound")}
                            </span>
                          </div>
                        )
                      }
                      divClasses="o-column-row-switcher"
                      pClasses="t-bold_semi-bold u-width--min-width_300"
                    />

                    <TextDiv
                      title="totalAmountFromInvoice"
                      value={
                        hasCurrencySymbol(analyzedData?.invoice?.totalAmount)
                          ? formatString(analyzedData?.invoice?.totalAmount)
                          : `${formatString(
                              analyzedData?.invoice?.totalAmount
                            )} ${analyzedData?.invoice?.currency}`
                      }
                      divClasses="o-column-row-switcher"
                      pClasses="t-bold_semi-bold u-width--min-width_300"
                    />
                    <TextDiv
                      title="calculatedTotalPrice"
                      value={`${
                        analyzedData?.invoice?.calculatedTotalPrice || ""
                      } ${analyzedData?.invoice?.currency || ""}`}
                      divClasses="o-column-row-switcher"
                      pClasses="t-bold_semi-bold u-width--min-width_300"
                    />

                    <RenderMatchStatus
                      value1={extractNumericValue(
                        analyzedData?.invoice?.totalAmount
                      )}
                      value2={parseFloat(
                        analyzedData?.invoice?.calculatedTotalPrice
                      )}
                      matchText="itemTotalAmountMatch"
                      spanStyle="u-width--min-width_300"
                    />
                  </div>
                </section>

                {/* Items section */}
                <section className="u-margin--top_500">
                  <SectionDivider title={"items"} noMargin={true} />
                  {analyzedData?.invoice?.hscode && (
                    <div className="o-row u-margin--top_500">
                      <TextDiv
                        title={"hsCode"}
                        value={`${analyzedData?.invoice?.hscode}`}
                        divClasses="o-column-row-switcher"
                        pClasses="t-bold_semi-bold u-width--min-width_300"
                      />
                      <Tooltip
                        direction="ltr"
                        textAlign="left"
                        maxWidth="400px"
                        content={
                          loadingHsCode ? (
                            <Loader size={LOADER_SIZES.SMALL} />
                          ) : (
                            <HsCodeViewer hsCodes={invoiceHsCodes} />
                          )
                        }
                        onShow={() => {
                          if (
                            analyzedData?.invoice?.hscode &&
                            invoiceHsCodes.length === 0
                          ) {
                            getHsCodes(
                              analyzedData?.invoice?.hscode,
                              setInvoiceHsCodes
                            );
                          }
                        }}
                      >
                        <img src={infoBlue} className="tooltip-img" />
                      </Tooltip>
                    </div>
                  )}
                  {items?.map((item, index) => {
                    return (
                      <div
                        className="o-column o-column--gap-size_300 u-margin--top_600"
                        key={index}
                      >
                        <SectionDividerWithNoLine
                          title={`${t("item")} ${index + 1}`}
                          noMargin={true}
                          spanClasses="t-bold_extra-bold"
                        />

                        <TextDiv
                          title={"name/no"}
                          value={item["name/no"]}
                          divClasses="o-column-row-switcher"
                          pClasses="t-bold_semi-bold u-width--min-width_300"
                        />
                        <TextDiv
                          title="description"
                          value={item.description}
                          divClasses="o-column-row-switcher"
                          pClasses="t-bold_semi-bold u-width--min-width_300"
                        />

                        <div className="o-row">
                          <TextDiv
                            title="hsCode"
                            value={
                              item.hscode || (
                                <div className="o-row o-row--vertical-align_end o-row--gap-size_100">
                                  <FailedIcon width={20} height={20} />
                                  <span className={"not-working-website"}>
                                    {t("notFound")}
                                  </span>
                                </div>
                              )
                            }
                            divClasses="o-column-row-switcher"
                            pClasses="t-bold_semi-bold u-width--min-width_300"
                          />
                          {item?.hscode && (
                            <Tooltip
                              direction="ltr"
                              textAlign="left"
                              maxWidth="400px"
                              content={
                                loadingHsCode ? (
                                  <Loader size={LOADER_SIZES.SMALL} />
                                ) : (
                                  <HsCodeViewer
                                    hsCodes={itemHsCodes[index] || []}
                                  />
                                )
                              }
                              onShow={() => {
                                if (item?.hscode && !itemHsCodes[index]) {
                                  getHsCodes(item?.hscode, (codes) => {
                                    setItemHsCodes((prev) => ({
                                      ...prev,
                                      [index]: codes,
                                    }));
                                  });
                                }
                              }}
                            >
                              <img src={infoBlue} className="tooltip-img" />
                            </Tooltip>
                          )}
                        </div>

                        <TextDiv
                          title="quantity"
                          value={`${item.quantity} ${item.measuringUnit || ""}`}
                          divClasses="o-column-row-switcher"
                          pClasses="t-bold_semi-bold u-width--min-width_300"
                          spanClasses="dir-ltr"
                        />
                        <TextDiv
                          title="unitPrice"
                          value={
                            hasCurrencySymbol(item?.unitPrice)
                              ? formatString(item.unitPrice)
                              : `${formatString(item.unitPrice)} ${
                                  analyzedData?.invoice?.currency
                                }`
                          }
                          divClasses="o-column-row-switcher"
                          pClasses="t-bold_semi-bold u-width--min-width_300"
                        />
                        <TextDiv
                          title="totalAmountFromInvoice"
                          value={
                            hasCurrencySymbol(item.totalPrice)
                              ? formatString(item.totalPrice)
                              : `${formatString(item.totalPrice)} ${
                                  analyzedData?.invoice?.currency
                                }`
                          }
                          divClasses="o-column-row-switcher"
                          pClasses="t-bold_semi-bold u-width--min-width_300"
                        />

                        <TextDiv
                          title="calculatedTotalPrice"
                          value={`${item.calculatedPrice} ${analyzedData?.invoice?.currency}`}
                          divClasses="o-column-row-switcher"
                          pClasses="t-bold_semi-bold u-width--min-width_300"
                        />

                        <RenderMatchStatus
                          value1={extractNumericValue(item.totalPrice)}
                          value2={parseFloat(item.calculatedPrice)}
                          matchText="itemTotalAmountMatch"
                          spanStyle="u-width--min-width_300"
                        />
                      </div>
                    );
                  })}
                </section>
              </>
            }
          />
        </div>
      </div>
    </PageWrapper>
  );
};

export default CompanyAnalyzeApplicationPage;
